import { apiFetch } from '@/api/request'
import type { InfoStage } from '@/api/infoCenter'
import type { ApiResponse } from '@/types/verification'

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const PUBLIC_BASE = `${API_ORIGIN}/api/portal/info`
const ADMIN_BASE = `${API_ORIGIN}/api/admin/portal/info`

const fetchOptions: RequestInit = { credentials: 'include' }
const jsonOptions: RequestInit = {
  ...fetchOptions,
  headers: { 'Content-Type': 'application/json' },
}

export type InfoStatus = 'PUBLISHED' | 'HIDDEN'

export interface InfoFolder {
  idx: number
  parentIdx: number | null
  name: string
  sort: number
  status: InfoStatus
  createdAt?: string
  updatedAt?: string | null
}

export interface InfoDoc {
  idx?: number
  id: string
  folderIdx: number
  folderName?: string
  title: string
  summary: string
  cover?: string
  content?: string
  status?: InfoStatus
  sort: number
  createdAt: string
  updatedAt?: string | null
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다.')
  }
  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) throw new Error('요청 처리에 실패했습니다.')
  return data
}

function unwrap<T>(result: ApiResponse<T>, fallback: string): T {
  if (!result.success || result.data == null) throw new Error(result.message || fallback)
  return result.data
}

export async function fetchPublicInfo(query: { folder?: number; keyword?: string } = {}) {
  const params = new URLSearchParams()
  if (query.folder) params.set('folder', String(query.folder))
  if (query.keyword) params.set('keyword', query.keyword)
  const qs = params.toString()
  const result = await parseJson<{ folders: InfoFolder[]; docs: InfoDoc[]; stage?: InfoStage }>(
    await apiFetch(`${PUBLIC_BASE}${qs ? `?${qs}` : ''}`, fetchOptions),
  )
  return unwrap(result, '정보마당을 불러오지 못했습니다.')
}

export async function fetchPublicInfoDoc(code: string) {
  const result = await parseJson<{ doc: InfoDoc }>(
    await apiFetch(`${PUBLIC_BASE}/${encodeURIComponent(code)}`, fetchOptions),
  )
  return unwrap(result, '콘텐츠를 불러오지 못했습니다.').doc
}

export async function fetchAdminInfo(keyword = '') {
  const params = keyword ? `?keyword=${encodeURIComponent(keyword)}` : ''
  const result = await parseJson<{ folders: InfoFolder[]; docs: InfoDoc[]; stage?: InfoStage }>(
    await apiFetch(`${ADMIN_BASE}${params}`, fetchOptions),
  )
  return unwrap(result, '정보마당을 불러오지 못했습니다.')
}

export async function saveInfoFolder(payload: Partial<InfoFolder> & { name: string }, idx?: number) {
  const result = await parseJson<{ folder: InfoFolder }>(
    await apiFetch(idx ? `${ADMIN_BASE}/folders/${idx}` : `${ADMIN_BASE}/folders`, {
      ...jsonOptions,
      method: idx ? 'PUT' : 'POST',
      body: JSON.stringify(payload),
    }),
  )
  return unwrap(result, '폴더 저장에 실패했습니다.').folder
}

export async function removeInfoFolder(idx: number) {
  const result = await parseJson<null>(
    await apiFetch(`${ADMIN_BASE}/folders/${idx}`, { ...fetchOptions, method: 'DELETE' }),
  )
  if (!result.success) throw new Error(result.message || '폴더 삭제에 실패했습니다.')
}

export async function saveInfoDoc(
  payload: { title: string; folderIdx: number; content: string; summary?: string; status?: InfoStatus; sort?: number },
  idx?: number,
) {
  const result = await parseJson<{ doc: InfoDoc }>(
    await apiFetch(idx ? `${ADMIN_BASE}/docs/${idx}` : `${ADMIN_BASE}/docs`, {
      ...jsonOptions,
      method: idx ? 'PUT' : 'POST',
      body: JSON.stringify(payload),
    }),
  )
  return unwrap(result, '콘텐츠 저장에 실패했습니다.').doc
}

export async function removeInfoDoc(idx: number) {
  const result = await parseJson<null>(
    await apiFetch(`${ADMIN_BASE}/docs/${idx}`, { ...fetchOptions, method: 'DELETE' }),
  )
  if (!result.success) throw new Error(result.message || '콘텐츠 삭제에 실패했습니다.')
}

export async function reorderInfo(kind: 'folders' | 'docs', items: Array<Record<string, number | null>>) {
  const result = await parseJson<null>(
    await apiFetch(`${ADMIN_BASE}/${kind}/reorder`, {
      ...jsonOptions,
      method: 'PUT',
      body: JSON.stringify({ items }),
    }),
  )
  if (!result.success) throw new Error(result.message || '순서 저장에 실패했습니다.')
}

/** 문서를 다른 폴더로 옮긴다. 본문은 건드리지 않고 소속 폴더만 바꾼다. */
export async function moveInfoDoc(idx: number, folderIdx: number) {
  await reorderInfo('docs', [{ idx, folderIdx }])
}

/** 폴더를 다른 상위 폴더(또는 최상위)로 옮긴다. */
export async function moveInfoFolder(idx: number, parentIdx: number | null) {
  await reorderInfo('folders', [{ idx, parentIdx }])
}
